import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CreateStartupForm } from "@/components/startup/CreateStartupForm";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

const EditStartup = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: startup, isLoading } = useQuery({
    queryKey: ['startup', id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('startups')
        .select(`
          *,
          startup_roles (
            id,
            title,
            description
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Startup not found');
      
      // Check if the user owns this startup
      if (data.user_id !== user?.id) {
        toast({
          title: "Unauthorized",
          description: "You don't have permission to edit this startup",
          variant: "destructive"
        });
        navigate('/communities/startups/dashboard');
        return null;
      }

      return {
        ...data,
        roles: data.startup_roles || []
      };
    },
    enabled: !!id && !!user
  });

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-flux-cyan" />
      </div>
    );
  }

  if (!startup) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <Card className="max-w-2xl mx-auto bg-flux-black/50 border-flux-cyan/20">
        <CardHeader>
          <CardTitle className="text-2xl text-flux-cyan">Edit Startup</CardTitle>
        </CardHeader>
        <CardContent>
          <CreateStartupForm initialData={startup} />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default EditStartup;